import { useState } from 'react';
import { Copy, Check, KeyRound, RefreshCw } from 'lucide-react';
import './PasswordGenerator.css';

const PasswordGenerator = () => {
  const [password, setPassword] = useState('');
  const [length, setLength] = useState(16);
  const [options, setOptions] = useState({
    uppercase: true,
    lowercase: true,
    numbers: true,
    symbols: true
  });
  const [copied, setCopied] = useState(false);
  const [strength, setStrength] = useState('');

  const generatePassword = () => {
    let charset = '';
    if (options.uppercase) charset += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    if (options.lowercase) charset += 'abcdefghijklmnopqrstuvwxyz';
    if (options.numbers) charset += '0123456789';
    if (options.symbols) charset += '!@#$%^&*()_+-=[]{}|;:,.<>?';

    if (charset === '') {
      alert('Please select at least one character type!');
      return;
    }

    let newPassword = '';
    const array = new Uint32Array(length);
    crypto.getRandomValues(array);

    for (let i = 0; i < length; i++) {
      newPassword += charset[array[i] % charset.length];
    }

    setPassword(newPassword);
    calculateStrength(newPassword);
    setCopied(false);
  };

  const calculateStrength = (pwd) => {
    if (!pwd) {
      setStrength('');
      return;
    }
    let score = 0;
    if (pwd.length >= 8) score++;
    if (pwd.length >= 12) score++;
    if (pwd.length >= 16) score++;
    if (/[a-z]/.test(pwd)) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[0-9]/.test(pwd)) score++;
    if (/[^a-zA-Z0-9]/.test(pwd)) score++;

    if (score <= 3) setStrength('weak');
    else if (score <= 5) setStrength('medium');
    else setStrength('strong');
  };

  const copyToClipboard = () => {
    if (!password) return;
    navigator.clipboard.writeText(password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleOptionChange = (option) => {
    setOptions(prev => ({ ...prev, [option]: !prev[option] }));
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setPassword(val);
    calculateStrength(val);
  };

  return (
    <div className="pw-container">
      <div className="pw-display">
        <input
          type="text"
          value={password}
          onChange={handleInputChange}
          placeholder="Type or click Generate to create password..."
          className="pw-input"
        />
        {password && (
          <button onClick={copyToClipboard} className="btn btn-primary pw-copy-btn">
            {copied ? <Check size={16} /> : <Copy size={16} />}
            <span>{copied ? 'Copied' : 'Copy'}</span>
          </button>
        )}
      </div>

      {password && (
        <div className={`pw-strength pw-${strength}`}>
          <div className="pw-strength-bar">
            <div className="pw-strength-fill"></div>
          </div>
          <span className="pw-strength-label">
            Security Rating: <strong>{strength.toUpperCase()}</strong>
          </span>
        </div>
      )}

      <div className="pw-control">
        <div className="pw-control-label">
          <span>Password Length</span>
          <span className="length-num">{length} Characters</span>
        </div>
        <input
          type="range"
          min="8"
          max="32"
          value={length}
          onChange={(e) => setLength(Number(e.target.value))}
          className="pw-slider"
        />
      </div>

      <div className="pw-options">
        <label className="pw-option">
          <input
            type="checkbox"
            checked={options.uppercase}
            onChange={() => handleOptionChange('uppercase')}
          />
          <span>Uppercase (A-Z)</span>
        </label>
        <label className="pw-option">
          <input
            type="checkbox"
            checked={options.lowercase}
            onChange={() => handleOptionChange('lowercase')}
          />
          <span>Lowercase (a-z)</span>
        </label>
        <label className="pw-option">
          <input
            type="checkbox"
            checked={options.numbers}
            onChange={() => handleOptionChange('numbers')}
          />
          <span>Numbers (0-9)</span>
        </label>
        <label className="pw-option">
          <input
            type="checkbox"
            checked={options.symbols}
            onChange={() => handleOptionChange('symbols')}
          />
          <span>Symbols (!@#$)</span>
        </label>
      </div>

      <button onClick={generatePassword} className="btn btn-primary pw-generate-btn">
        <KeyRound size={18} />
        <span>Generate Secure Password</span>
      </button>
    </div>
  );
};

export default PasswordGenerator;
